import React from 'react';
import { motion } from 'framer-motion';
import { PhotoIcon } from '@heroicons/react/24/outline';
import { FieldErrors, UseFormRegister } from 'react-hook-form';
import ImageUpload from './ImageUpload';
import { ListingFormData } from '../types';

interface MediaUploadProps {
  register: UseFormRegister<ListingFormData>;
  errors: FieldErrors<ListingFormData>;
  storedImages: FileList | null;
  onImageChange: (files: FileList | null) => void;
  onImageRemove: (index: number) => void;
  previewUrls: string[];
}

const MediaUpload: React.FC<MediaUploadProps> = ({
  register,
  errors,
  storedImages,
  onImageChange,
  onImageRemove,
  previewUrls
}) => {
  const imageCount = previewUrls.length;

  return (
    <motion.div
      key="singleStep5"
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      className="space-y-6"
    >
      {/* Progress indicator */}
      <div className="bg-indigo-50 border-l-4 border-indigo-600 p-4 rounded-r-lg mb-6">
        <p className="text-sm text-indigo-800">
          <strong>Step 5 of 6:</strong> Show it off with photos! One final step
          after this
        </p>
      </div>

      <h2 className="text-2xl font-semibold mb-6 flex items-center">
        <PhotoIcon className="w-6 h-6 mr-2 text-blue-500" />
        Media
      </h2>
      <div className="space-y-6">
        <div>
          <label className="block mb-2 font-medium text-gray-700">
            Property Images <span className="text-red-500">*</span>
          </label>
          <p className="text-sm text-gray-500 mb-2">
            Upload 5-10 high-quality photos.{' '}
            <strong>Listings with 8+ photos get 60% more inquiries!</strong>{' '}
            Show every room, natural lighting works best.
          </p>
          {imageCount > 0 && (
            <p className="text-sm font-medium mb-2">
              {imageCount < 5 ? (
                <span className="text-orange-600">
                  📸 {imageCount} uploaded - Add {5 - imageCount} more for best
                  results
                </span>
              ) : imageCount < 8 ? (
                <span className="text-blue-600">
                  ✓ {imageCount} uploaded - Great! Consider adding{' '}
                  {8 - imageCount} more
                </span>
              ) : (
                <span className="text-green-600">
                  ✓ {imageCount} uploaded - Perfect! Your listing will stand out
                </span>
              )}
            </p>
          )}
          <ImageUpload
            files={storedImages}
            onChange={onImageChange}
            onRemove={onImageRemove}
            previewUrls={previewUrls}
            error={errors.images?.message}
            multiple={true}
            accept="image/jpeg,image/png,image/gif,image/heic,image/heif,.heic,.heif"
          />
        </div>
        <div>
          <label className="block mb-2 font-medium text-gray-700">
            Video URL (optional)
          </label>
          <p className="text-sm text-gray-500 mb-2">
            Add a YouTube walkthrough video. Videos increase engagement by{' '}
            <strong>85%</strong>
          </p>
          <input
            {...register('videoUrl', {
              pattern: {
                value: /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.?be)\/.+$/,
                message: 'Enter a valid YouTube URL'
              }
            })}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none transition duration-300 ${
              errors.videoUrl ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="e.g., https://www.youtube.com/watch?v=example"
          />
          {errors.videoUrl && (
            <span className="text-red-500 text-sm">
              {errors.videoUrl.message}
            </span>
          )}
        </div>

        {/* Help text */}
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg">
          <p className="text-sm text-blue-800">
            <strong>📷 Photo tips:</strong> Include kitchen, bathroom, bedrooms,
            living areas. Capture during daytime for natural light. Declutter
            spaces before shooting!
          </p>
        </div>
      </div>
    </motion.div>
  );
};

export default MediaUpload;
